import {
  useRef,
  useSyncExternalStore,
} from "@bestowinc/tinkertoy-shell-deps/react";

import type {
  FlowMeta,
  LoadedViewResponse,
  WireEarlyLayout,
  WireLoadingModule,
} from "@bestowinc/tinkertoy-core";

import type { LoadingSlot } from "./loading-slot";

/**
 * What the shell renders while loading:
 * - `layout` — the streamed layout with the loading module inside it
 * - `page` — the loading module on its own (no early layout is coming)
 * - `waiting` — a loading module is known but an early layout may follow
 */
export type LoadingDisplay =
  | {
      kind: "layout";
      loadingModule: WireLoadingModule;
      layout: WireEarlyLayout;
      loadedView: LoadedViewResponse;
      flowMeta?: FlowMeta;
    }
  | { kind: "page"; loadingModule: WireLoadingModule; flowMeta?: FlowMeta }
  | { kind: "waiting"; loadingModule: WireLoadingModule; flowMeta?: FlowMeta };

const RANK: Record<LoadingDisplay["kind"], number> = {
  waiting: 0,
  page: 1,
  layout: 2,
};

/**
 * Reads the loading slot and holds what it shows while `active`:
 *
 * - The first loading module seen stays for the rest of the load, so a
 *   redirect hop never swaps one loading module for another.
 * - The display only upgrades (waiting → page → layout), and survives the
 *   slot emptying between redirect hops.
 * - Everything resets once `active` goes false, or once a load that
 *   superseded the held one reports.
 */
export function useHeldLoadingDisplay(
  slot: LoadingSlot,
  active: boolean,
): LoadingDisplay | undefined {
  const snapshot = useSyncExternalStore(slot.subscribe, slot.getSnapshot);
  const held = useRef<{ loadId: number; display: LoadingDisplay } | undefined>(
    undefined,
  );

  if (!active) {
    held.current = undefined;
    return undefined;
  }
  // Another load superseded the held one: keep showing it until the new load
  // reports, so a same-module supersede doesn't flash the old view.
  if (
    held.current !== undefined &&
    held.current.loadId !== snapshot.loadId &&
    snapshot.reported === true
  ) {
    held.current = undefined;
  }

  const previous = held.current?.display;
  const { loadingModule } = snapshot;
  if (loadingModule === undefined) {
    return previous;
  }

  const shown = previous?.loadingModule ?? loadingModule;
  const next: LoadingDisplay =
    snapshot.layout !== undefined && snapshot.loadedView !== undefined
      ? {
          kind: "layout",
          loadingModule: shown,
          layout: snapshot.layout,
          loadedView: snapshot.loadedView,
          flowMeta: snapshot.flowMeta,
        }
      : snapshot.earlyLayout === false
      ? { kind: "page", loadingModule: shown, flowMeta: snapshot.flowMeta }
      : { kind: "waiting", loadingModule: shown, flowMeta: snapshot.flowMeta };

  if (previous === undefined || RANK[next.kind] >= RANK[previous.kind]) {
    held.current = { loadId: snapshot.loadId, display: next };
    return next;
  }
  return previous;
}
